import { useCallback, useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useExchangeIdentityTokenMutation } from "../../types.g";
import { ssm } from "../../state/session/ssm";

interface TokenData {
  token: string;
  expiresAt: number;
}

export function useIdentityToken(): string | null {
  const [exchangeIdentityToken] = useExchangeIdentityTokenMutation();
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [spaceId, setSpaceId] = useState<string | null>(null);

  useEffect(() => {
    if (ssm.current.settings.spaceId) setSpaceId(ssm.current.settings.spaceId);
  }, [ssm.current.settings.spaceId]);

  const fetchToken = useCallback(
    async (signal: { cancelled: boolean }) => {
      if (!spaceId) {
        console.warn("[RDM] useIdentityToken: Space ID is not set");
        return;
      }
      try {
        const { data } = await exchangeIdentityToken({
          variables: { spaceId },
        });

        if (signal.cancelled) return;

        const payload = data?.exchangeIdentityToken;

        if (payload?.token) {
          const { exp } = jwtDecode<{ exp: number }>(payload.token);
          setTokenData({
            token: payload.token,
            expiresAt: exp * 1000,
          });
        }
      } catch {
        console.error(
          "[RDM] useIdentityToken: Error exchanging identity token for space ID",
        );
        // Token stays null; consumer treats missing token as disconnected / not ready.
      }
    },
    [exchangeIdentityToken, spaceId],
  );

  const isExpired = tokenData && Date.now() >= tokenData?.expiresAt;

  useEffect(() => {
    const signal = { cancelled: false };
    const needsFetch = spaceId && (!tokenData || isExpired);

    if (needsFetch) void fetchToken(signal);

    return () => {
      signal.cancelled = true;
    };
  }, [fetchToken, spaceId, tokenData, isExpired]);

  if (!tokenData || isExpired) return null;

  return tokenData.token;
}
